import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Plus, ArrowLeftRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Transfers() {
    const [items, setItems] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ from_account_id: "", to_account_id: "", amount: 0, date: today(), reference: "" });

    const load = async () => {
        const [t, a] = await Promise.all([api.get("/transfers"), api.get("/bank-accounts")]);
        setItems(t.data); setAccounts(a.data);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.from_account_id || !form.to_account_id || !form.amount) return toast.error("Fill all fields");
        await api.post("/transfers", { ...form, amount: Number(form.amount) });
        toast.success("Transfer recorded");
        setOpen(false);
        await load();
    };

    return (
        <>
            <PageHeader kicker="BANKING" title="Transfers" subtitle="Internal movements between bank and cash accounts."
                actions={<Button onClick={() => setOpen(true)} data-testid="transfer-new"><Plus className="w-4 h-4 mr-1.5" />New transfer</Button>} />
            <PageBody>
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {items.length === 0 ? <EmptyState icon={ArrowLeftRight} title="No transfers yet" /> :
                        <table className="fin-table">
                            <thead><tr><th>Date</th><th>From</th><th>To</th><th className="num">Amount</th><th>Reference</th></tr></thead>
                            <tbody>
                                {items.map((t) => {
                                    const f = accounts.find((a) => a.id === t.from_account_id);
                                    const tt = accounts.find((a) => a.id === t.to_account_id);
                                    return (
                                        <tr key={t.id}>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(t.date)}</td>
                                            <td>{f?.name}</td>
                                            <td>{tt?.name}</td>
                                            <td className="num">{fmtMoney(t.amount)}</td>
                                            <td className="font-mono text-[12px]">{t.reference || "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>}
                </div>
            </PageBody>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-[420px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4"><SheetTitle className="text-xl font-semibold tracking-tight">New transfer</SheetTitle></SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-4" onSubmit={submit}>
                        <div><Label>From</Label>
                            <Select value={form.from_account_id} onValueChange={(v) => setForm({ ...form, from_account_id: v })}>
                                <SelectTrigger className="mt-1.5"><SelectValue placeholder="From account" /></SelectTrigger>
                                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>To</Label>
                            <Select value={form.to_account_id} onValueChange={(v) => setForm({ ...form, to_account_id: v })}>
                                <SelectTrigger className="mt-1.5"><SelectValue placeholder="To account" /></SelectTrigger>
                                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                        <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" /></div>
                        <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" /></div>
                        <Button type="submit" className="w-full">Save transfer</Button>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
}
